// =============================================================================
// features/sales/contacts/ContactAttemptsHistory.tsx — Histórico de
// tentativas de contato do lead, com desfecho legível (doc 26 §6.3 DoD do
// slot F34-S05: "Tentativas aparecem na timeline do lead com desfecho
// legível").
//
// POR QUE NÃO MESCLAR na "Timeline" de `CrmDetailPage.tsx`
// (`mergedInteractions`, que já mescla compromissos de F33-S07): criar uma
// tentativa (`POST /contact-attempts`) já grava, no MESMO instante, um
// resumo GENÉRICO em `interactions` (apps/api/.../attempts/service.ts —
// "Tentativa de contato registrada (canal: X, origem: Y)"), sem o desfecho
// (que só existe depois do 2º POST, `/outcome`). Misturar essa linha
// genérica com uma segunda linha client-side já mostrando o desfecho
// duplicaria a mesma tentativa na Timeline (uma sem contexto, outra com).
//
// Em vez disso, este componente segue o MESMO padrão de
// `features/crm/components/SimulationHistory.tsx`: uma seção própria,
// abaixo dos dados de contato, com o histórico COMPLETO e correto (canal +
// desfecho + observação) — sem depender do texto fixo que o backend grava na
// timeline genérica.
// =============================================================================

import type { ContactAttempt } from '@elemento/shared-schemas';
import { SALES_OUTCOME_META } from '@elemento/shared-schemas';
import * as React from 'react';

import { Badge } from '../../../components/ui/Badge';
import { formatRelativeDate } from '../../../hooks/crm/types';
import { useLeadContactAttemptsTimeline } from '../../../hooks/sales/useContactAttempts';
import { cn } from '../../../lib/cn';
import { describeAttemptOrigin } from '../components/CampaignMembershipCard';

import { CHANNEL_LABELS } from './labels';

/**
 * Teto de altura da lista de tentativas de contato (F49-S01) — bloco
 * secundário (`40vh`). `max-h`, nunca `h-`: lista curta não vira caixa vazia.
 */
export const ATTEMPTS_SCROLL_CLASS = 'max-h-[40vh] overflow-y-auto pr-1';

// ---------------------------------------------------------------------------
// Formatação de uma tentativa em texto legível — exportada separada do
// componente para ser testável sem renderizar React.
//
// `originLabel` (F38-S06, doc planejamento-livechat-contexto-comercial.md
// §3-B): mudança ADITIVA — campo sempre presente no `ContactAttempt`
// (`origin` é obrigatório no schema desde F34-S01), só passa a ser EXIBIDO
// aqui. Consumidores existentes (CRM, operação) continuam recebendo o mesmo
// shape de `describeAttempt`, com um campo a mais — nada quebra.
// ---------------------------------------------------------------------------

export function describeAttempt(attempt: ContactAttempt): {
  channelLabel: string;
  outcomeLabel: string | null;
  isPending: boolean;
  originLabel: string;
} {
  const channelLabel = CHANNEL_LABELS[attempt.channel];
  const originLabel = describeAttemptOrigin(attempt.origin);
  if (attempt.outcomeCode === null) {
    return { channelLabel, outcomeLabel: null, isPending: true, originLabel };
  }
  // Rótulo de exibição de um desfecho HISTÓRICO — mesma fonte de rótulos que
  // o resto do módulo comercial usa para valores já registrados (não é a
  // lista de opções do formulário, que vem de GET /api/sales/outcomes; aqui
  // é só o texto legível de um código já gravado, doc 26 §6.3).
  const label = SALES_OUTCOME_META[attempt.outcomeCode]?.label ?? attempt.outcomeCode;
  return { channelLabel, outcomeLabel: label, isPending: false, originLabel };
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function AttemptSkeleton(): React.JSX.Element {
  return (
    <div
      className="rounded-md border border-border bg-surface-1 p-4 animate-pulse"
      style={{ boxShadow: 'var(--elev-1)' }}
    >
      <div className="h-3 w-24 rounded-xs mb-2" style={{ background: 'var(--surface-muted)' }} />
      <div className="h-4 w-full rounded-xs" style={{ background: 'var(--surface-muted)' }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card de tentativa
// ---------------------------------------------------------------------------

function AttemptCard({ attempt }: { attempt: ContactAttempt }): React.JSX.Element {
  const { channelLabel, outcomeLabel, isPending, originLabel } = describeAttempt(attempt);

  return (
    <div
      className="rounded-md border border-border bg-surface-1 p-4"
      style={{ boxShadow: 'var(--elev-1)' }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1 min-w-0">
          <p className="font-sans text-sm font-semibold text-ink">
            {channelLabel}
            {outcomeLabel && <span className="text-ink-3"> · {outcomeLabel}</span>}
          </p>
          <p className="font-sans text-xs text-ink-4">Origem: {originLabel}</p>
          {attempt.notes && (
            <p className="font-sans text-xs text-ink-3 leading-relaxed">{attempt.notes}</p>
          )}
          {attempt.nextStep && (
            <p className="font-sans text-xs text-ink-3 leading-relaxed">
              Próximo passo: {attempt.nextStep}
            </p>
          )}
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1">
          <Badge variant={isPending ? 'warning' : 'neutral'}>
            {isPending ? 'Aguardando desfecho' : 'Concluída'}
          </Badge>
          <span className="font-sans text-xs text-ink-4">
            {formatRelativeDate(attempt.startedAt)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export interface ContactAttemptsHistoryProps {
  leadId: string;
  enabled?: boolean;
}

/**
 * Seção "Tentativas de contato" da ficha do lead — abaixo dos dados de
 * contato, mesma posição de `SimulationHistory`. Não renderiza nada quando
 * `enabled` é `false` (gate de permissão/flag) ou quando não há tentativas.
 */
export function ContactAttemptsHistory({
  leadId,
  enabled = true,
}: ContactAttemptsHistoryProps): React.JSX.Element | null {
  const { attempts, isLoading } = useLeadContactAttemptsTimeline(leadId, { enabled });

  if (!enabled) return null;
  if (!isLoading && attempts.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <h2
          className="font-display font-bold text-ink"
          style={{
            fontSize: 'var(--text-base)',
            letterSpacing: '-0.025em',
            fontVariationSettings: "'opsz' 16",
          }}
        >
          Tentativas de contato
        </h2>
        {!isLoading && attempts.length > 0 && (
          <span
            className="font-sans font-semibold rounded-pill px-2 py-0.5"
            style={{
              fontSize: '0.65rem',
              letterSpacing: '0.06em',
              color: 'var(--text-3)',
              background: 'var(--surface-muted)',
              boxShadow: 'var(--elev-1)',
            }}
          >
            {attempts.length}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          <AttemptSkeleton />
          <AttemptSkeleton />
        </div>
      ) : (
        <div className={cn('flex flex-col gap-2', ATTEMPTS_SCROLL_CLASS)}>
          {attempts.map((attempt, idx) => (
            <div
              key={attempt.id}
              style={{ animationDelay: `${idx * 40}ms` } as React.CSSProperties}
            >
              <AttemptCard attempt={attempt} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
